import { parseRelativeRoute } from "@/lib/auth/sanitize-return-to";

const INTERNAL_ORIGIN = "https://repackify.local";
const MAX_RETURN_CONTEXT_DEPTH = 6;
const MAX_RETURN_HREF_LENGTH = 8_192;
const MAX_CONTEXT_SCAN_DEPTH = 32;

type ProtectedSurface = "/admin" | "/dashboard";

function parseInternalHref(href: string): URL | null {
  const rawPath = href.split(/[?#]/, 1)[0];
  try {
    if (
      rawPath
        .split("/")
        .some((segment) => [".", ".."].includes(decodeURIComponent(segment)))
    ) {
      return null;
    }
  } catch {
    return null;
  }
  const route = parseRelativeRoute(href);
  return route ? new URL(route, INTERNAL_ORIGIN) : null;
}

function protectedSurface(pathname: string): ProtectedSurface | null {
  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    return "/admin";
  }
  if (pathname === "/dashboard" || pathname.startsWith("/dashboard/")) {
    return "/dashboard";
  }
  return null;
}

function toRelativeHref(url: URL): string {
  return `${url.pathname}${url.search}${url.hash}`;
}

function collectReturnChain(sourceUrl: URL, surface: ProtectedSurface) {
  const chain: string[] = [];
  const visited = new Set<string>();
  let currentUrl: URL | null = sourceUrl;

  while (currentUrl && chain.length < MAX_CONTEXT_SCAN_DEPTH) {
    const currentHref = toRelativeHref(currentUrl);
    if (visited.has(currentHref)) break;
    visited.add(currentHref);

    const nestedFrom: string | null = currentUrl.searchParams.get("from");
    currentUrl.searchParams.delete("from");
    chain.push(toRelativeHref(currentUrl));

    const nestedUrl: URL | null = nestedFrom
      ? parseInternalHref(nestedFrom)
      : null;
    currentUrl =
      nestedUrl && protectedSurface(nestedUrl.pathname) === surface
        ? nestedUrl
        : null;
  }

  if (chain.length <= MAX_RETURN_CONTEXT_DEPTH) return chain;
  return [
    ...chain.slice(0, MAX_RETURN_CONTEXT_DEPTH - 1),
    chain[chain.length - 1],
  ];
}

function buildReturnChain(chain: string[]): string {
  let returnHref = chain[chain.length - 1];
  for (let index = chain.length - 2; index >= 0; index -= 1) {
    const url = new URL(chain[index], INTERNAL_ORIGIN);
    url.searchParams.set("from", returnHref);
    returnHref = toRelativeHref(url);
  }
  return returnHref;
}

function attachBoundedReturnContext(destinationUrl: URL, chain: string[]) {
  const boundedChain = [...chain];
  while (boundedChain.length > 0) {
    destinationUrl.searchParams.set("from", buildReturnChain(boundedChain));
    const href = toRelativeHref(destinationUrl);
    if (href.length <= MAX_RETURN_HREF_LENGTH) return href;
    if (boundedChain.length === 1) break;
    const removableIndex =
      boundedChain.length === 2 ? 0 : boundedChain.length - 2;
    boundedChain.splice(removableIndex, 1);
  }
  destinationUrl.searchParams.delete("from");
  return toRelativeHref(destinationUrl);
}

export function withReturnContext(
  destinationHref: string,
  sourceHref: string,
): string {
  const destinationUrl = parseInternalHref(destinationHref);
  const sourceUrl = parseInternalHref(sourceHref);
  const destinationSurface = destinationUrl
    ? protectedSurface(destinationUrl.pathname)
    : null;
  if (
    !destinationUrl ||
    !sourceUrl ||
    !destinationSurface ||
    protectedSurface(sourceUrl.pathname) !== destinationSurface
  ) {
    return destinationHref;
  }

  return attachBoundedReturnContext(
    destinationUrl,
    collectReturnChain(sourceUrl, destinationSurface),
  );
}

export function withCurrentReturnContext(destinationHref: string): string {
  if (typeof window === "undefined") return destinationHref;
  return withReturnContext(
    destinationHref,
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
}

export function resolveReturnContext(
  from: string | null | undefined,
  fallbackHref: string,
): string {
  const fallbackUrl = parseInternalHref(fallbackHref);
  const fromUrl = from ? parseInternalHref(from) : null;
  const fallbackSurface = fallbackUrl
    ? protectedSurface(fallbackUrl.pathname)
    : null;
  if (
    !fromUrl ||
    !fallbackSurface ||
    protectedSurface(fromUrl.pathname) !== fallbackSurface
  ) {
    return fallbackHref;
  }

  return toRelativeHref(fromUrl);
}
